import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Input, BaseButton, Modal, ToolTip, useT } from '@repo/common';
import DegreeList from './DegreeList';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';

const EducationForm = ({ education, setResumeData }) => {
  const t = useT();
  const [educations, setEducations] = useState([
    {
      id: uuidv4(),
      schoolName: '',
      fieldOfStudy: '',
      degree: '',
      studyPeriod: '',
    },
  ]);
  const [isInitialized, setIsInitialized] = useState(false);

  const [openDegreeId, setOpenDegreeId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [hoverDragAndDropId, setHoverDragAndDropId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);
  const inputRefs = useRef({});
  const degreeDropdownRef = useRef(null);

  useEffect(() => {
    if (!isInitialized) {
      if (!education || education.length === 0) {
        setEducations([
          {
            id: uuidv4(),
            schoolName: '',
            fieldOfStudy: '',
            degree: '',
            studyPeriod: '',
          },
        ]);
      } else {
        setEducations(
          education.map((edu) => ({
            id: uuidv4(),
            schoolName: edu.schoolName || '',
            fieldOfStudy: edu.fieldOfStudy || '',
            degree: edu.degree || '',
            studyPeriod: edu.studyPeriod || '',
          })),
        );
      }
      setIsInitialized(true);
    }
  }, [education, isInitialized]);

  const addEducation = () => {
    const newEdu = {
      id: uuidv4(),
      schoolName: '',
      fieldOfStudy: '',
      degree: '',
      studyPeriod: '',
    };
    setEducations((prev) => [...prev, newEdu]);

    setTimeout(() => {
      inputRefs.current[`school-${newEdu.id}`]?.focus();
    }, 0);
  };

  const openConfirmModal = (id) => {
    setTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const updated = educations.filter((edu) => edu.id !== targetId);
    setEducations(updated);

    setResumeData((prev) => ({
      ...prev,
      education: updated,
    }));

    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleInputChange = (id, field, value) => {
    const newEducations = educations.map((edu) =>
      edu.id === id ? { ...edu, [field]: value } : edu,
    );
    setEducations(newEducations);

    setResumeData((prev) => ({
      ...prev,
      education: newEducations,
    }));
  };

  const toggleDegreeList = (id, e) => {
    e.stopPropagation();
    setOpenDegreeId((prev) => (prev === id ? null : id));
  };

  const handleSelectDegree = (id, degree) => {
    handleInputChange(id, 'degree', degree);
    setOpenDegreeId(null);
    inputRefs.current[id]?.blur();
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(educations);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);

    setEducations(items);

    setResumeData((prev) => ({
      ...prev,
      education: items,
    }));
  };

  const didFocus = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (didFocus.current) return;
    if (educations.length === 0) return;

    const firstId = educations[0].id;
    setTimeout(() => {
      inputRefs.current[`school-${firstId}`]?.focus();
      didFocus.current = true;
    }, 0);
  }, [educations]);

  useEffect(() => {
    if (!openDegreeId) return;

    const handleClickOutside = (e) => {
      if (
        degreeDropdownRef.current &&
        !degreeDropdownRef.current.contains(e.target)
      ) {
        setOpenDegreeId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDegreeId]);

  return (
    <div className='flex flex-col items-start w-full min-w-[460px] max-w-[950px] gap-6'>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='educations' direction='vertical'>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex flex-col gap-6 w-full'
            >
              {educations.map((edu, index) => (
                <Draggable key={edu.id} draggableId={edu.id} index={index}>
                  {(provided, snapshot) => {
                    const style = {
                      ...provided.draggableProps.style,
                      transform: provided.draggableProps.style?.transform
                        ? provided.draggableProps.style.transform.replace(
                            /translate\(([-\d.]+)px,/,
                            'translate(0px,',
                          )
                        : undefined,
                    };

                    if (!snapshot.isDragging) {
                      delete style.transition;
                    }

                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex w-full items-start`}
                      >
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className='relative flex-shrink-0 pt-2 cursor-grab active:cursor-grabbing text-grayscale-gray03 hover:text-icon-success active:text-icon-success'
                          onMouseEnter={() => setHoverDragAndDropId(edu.id)}
                          onMouseLeave={() => setHoverDragAndDropId(null)}
                        >
                          <DragAndDropIcon width={33} height={33} />
                          {hoverDragAndDropId === edu.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t(
                                  'Click and drag to move',
                                  '클릭한 채로 드래그하여 이동하세요',
                                )}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>

                        {/* Education Form */}
                        <div className='flex-grow bg-grayscale-gray00 border border-grayscale-gray02 rounded-[8px] p-6'>
                          <div className='grid w-full grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-6'>
                            {/* School Name */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t('School name', '학교명')}
                                ref={(el) =>
                                  (inputRefs.current[`school-${edu.id}`] = el)
                                }
                                placeholder={t(
                                  'e.g. Seoul National University',
                                  '예: 서울대학교',
                                )}
                                value={edu.schoolName}
                                onChange={(e) =>
                                  handleInputChange(
                                    edu.id,
                                    'schoolName',
                                    e.target.value,
                                  )
                                }
                                hideCount
                              />
                            </div>

                            {/* Field of Study */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t('Field of study', '전공')}
                                placeholder={t('e.g. Design', '예: 디자인')}
                                value={edu.fieldOfStudy}
                                onChange={(e) =>
                                  handleInputChange(
                                    edu.id,
                                    'fieldOfStudy',
                                    e.target.value,
                                  )
                                }
                                hideCount
                              />
                            </div>

                            {/* Degree */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <div
                                ref={degreeDropdownRef}
                                onClick={(e) => toggleDegreeList(edu.id, e)}
                                className='relative'
                              >
                                <Input
                                  ref={(el) => (inputRefs.current[edu.id] = el)}
                                  label={t('Degree', '학위')}
                                  placeholder={t(
                                    'e.g. Bachelor’s, Master’s',
                                    '예: 학사, 석사',
                                  )}
                                  value={edu.degree}
                                  readOnly
                                  hideCount
                                  rightIcon={
                                    openDegreeId === edu.id ? (
                                      <ArrowTopIcon />
                                    ) : (
                                      <ArrowBottomIcon />
                                    )
                                  }
                                />
                                {openDegreeId === edu.id && (
                                  <div className='absolute top-[80px] left-0 z-20 w-full'>
                                    <DegreeList
                                      onSelect={(deg) =>
                                        handleSelectDegree(edu.id, deg)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Study Period */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t('Study period', '재학 기간')}
                                placeholder={t(
                                  'e.g. 2018.03 - Present',
                                  '예: 2018.03 - 현재',
                                )}
                                value={edu.studyPeriod}
                                onChange={(e) =>
                                  handleInputChange(
                                    edu.id,
                                    'studyPeriod',
                                    e.target.value,
                                  )
                                }
                                hideCount
                              />
                            </div>
                          </div>
                        </div>

                        {/* Delete */}
                        <div className='flex-shrink-0 pt-1 relative'>
                          <button
                            className='w-10 h-10 flex justify-center items-center text-grayscale-gray04 hover:text-icon-success transition-colors'
                            onClick={() => openConfirmModal(edu.id)}
                            onMouseEnter={() => setHoverDeleteId(edu.id)}
                            onMouseLeave={() => setHoverDeleteId(null)}
                          >
                            <DeleteIcon width={24} height={24} />
                          </button>
                          {hoverDeleteId === edu.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t('Delete', '삭제')}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className='w-full pl-[25px] pr-[40px]'>
        <BaseButton
          type='secondary'
          size='l'
          icon={<PlusIcon />}
          width='100%'
          onClick={addEducation}
          textWrapperClass='flex-none'
        >
          {t('Add one more Education', '학력 추가하기')}
        </BaseButton>
      </div>

      {showDeleteModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t(
            'Delete this education entry?',
            '이 학력 항목을 삭제할까요?',
          )}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 되돌릴 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default EducationForm;
